import { FormControl, InputAdornment, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { Controller, type UseFormReturn } from 'react-hook-form';
import Alert from '/@/components/bayer/alert';
import type useUnitProvider from '/@/core/context/unit';
import { _ } from '/@/locale';
import MuiDatePicker from '/@/mui/datepicker';
import type { CropMonitoring, CropType } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import { getCrops } from '/@/views/dashboard/hooks/crops';
import { MAX_ENVIRONMENT, MIN_ENVIRONMENT } from '../../creation-dialog';
import EnvironmentInput from '../../creation-dialog/components/EnvironmentInput';
import type { DateOption, FormValues, PlantingDateBounds } from '../hooks/use-report-form';

const CreateReportForm = ({
    cropMonitoring,
    field,
    form,
    units,
    plantingDateOptions,
    harvestDateOptions,
    plantingDateBounds,
    plantingDisabled,
    bothAutoSelected,
}: {
    cropMonitoring: CropMonitoring;
    field: Field;
    form: UseFormReturn<FormValues>;
    units: ReturnType<typeof useUnitProvider>;
    plantingDateOptions: DateOption[];
    harvestDateOptions: DateOption[];
    plantingDateBounds: PlantingDateBounds;
    plantingDisabled: boolean;
    bothAutoSelected: boolean;
}) => {
    const crops = getCrops();
    const campaign = cropMonitoring?.campaign;
    const fieldIsFieldview = field.externalProvider === 'fieldview';

    const harvestDate = form.watch('harvestDate');
    const renderPlantingDateSelector = Boolean(plantingDateOptions.length && form.watch('plantingResultId'));
    const renderHarvestDateSelector = Boolean(harvestDateOptions.length && form.watch('harvestResultId'));

    // ±1 month around the campaign planting date, never past the harvest date.
    const plantingMaxDate = (() => {
        const upperFromHarvest = harvestDate ?? dayjs();
        if (plantingDateBounds.maxDate) {
            return plantingDateBounds.maxDate.isAfter(upperFromHarvest) ? upperFromHarvest : plantingDateBounds.maxDate;
        }
        return upperFromHarvest;
    })();

    return (
        <Stack spacing={3} textAlign='left'>
            {!fieldIsFieldview && (
                <Alert severity='danger'>
                    {_(
                        'Este lote no está en FieldView, fue creado en Experto. Completá los datos manualmente para generar el reporte.'
                    )}
                </Alert>
            )}

            <Typography variant='body1' color='primary.mui.inactive'>
                {bothAutoSelected
                    ? _('Revisá y confirmá los datos de la cosecha.')
                    : _('Vamos a generar un reporte con los resultados del lote para esta campaña.')}
            </Typography>

            <Stack spacing={0.5} p={2} sx={{ background: '#f5f5f5' }}>
                <Stack direction='row' spacing={0.5}>
                    <Typography variant='body2' color='primary' fontWeight='bold'>
                        {_('Cultivo:')}
                    </Typography>
                    <Typography variant='body2' color='primary.mui.inactive'>
                        {crops[cropMonitoring?.crop as keyof typeof crops]?.name}
                    </Typography>
                </Stack>

                <Stack direction='row' spacing={0.5}>
                    <Typography variant='body2' color='primary' fontWeight='bold'>
                        {_('Híbrido:')}
                    </Typography>
                    <Typography variant='body2' color='primary.mui.inactive'>
                        {cropMonitoring?.productName}
                    </Typography>
                </Stack>

                <Stack direction='row' spacing={0.5}>
                    <Typography variant='body2' color='primary' fontWeight='bold'>
                        {_('Lote:')}
                    </Typography>
                    <Typography variant='body2' color='primary.mui.inactive'>
                        {field?.name}
                    </Typography>
                </Stack>

                <Stack direction='row' spacing={0.5}>
                    <Typography variant='body2' color='primary' fontWeight='bold'>
                        {_('Campaña:')}
                    </Typography>
                    <Typography variant='body2' color='primary.mui.inactive'>
                        {campaign ? `${Number(campaign)}/${Number(campaign) + 1}` : ''}
                    </Typography>
                </Stack>
            </Stack>

            <Stack>
                <Typography color='primary' variant='subtitle1' fontWeight='bold'>
                    {_('Fecha de siembra')}
                </Typography>
                {renderPlantingDateSelector && (
                    <Controller
                        name='plantingResultId'
                        control={form.control}
                        rules={{ required: _('Campo requerido') }}
                        render={({ field: plantingField, fieldState }) => (
                            <FormControl>
                                <Select
                                    {...plantingField}
                                    placeholder={_('Seleccione una fecha')}
                                    disabled={plantingDisabled}
                                    onChange={(e) => {
                                        const selected = plantingDateOptions.find(
                                            (opt) => opt.value === e.target.value
                                        );
                                        if (selected) {
                                            form.setValue('plantingDate', selected.date);
                                            form.setValue('plantingRequestId', selected.requestId);
                                            plantingField.onChange(e);
                                        }
                                    }}
                                    error={Boolean(fieldState.error?.message)}
                                    defaultValue={null}
                                    sx={{ '& .MuiSelect-select': { whiteSpace: 'unset !important' } }}
                                >
                                    {plantingDateOptions.map((pd) => (
                                        <MenuItem
                                            key={pd.value}
                                            value={pd.value}
                                            disabled={Boolean(harvestDate && pd.date.isAfter(harvestDate))}
                                        >
                                            {pd.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <Typography variant='caption' color='error' sx={{ ml: '14px', mt: '3px' }}>
                                    {fieldState.error?.message}
                                </Typography>
                            </FormControl>
                        )}
                    />
                )}
                {!renderPlantingDateSelector && (
                    <Controller
                        name='plantingDate'
                        control={form.control}
                        rules={{
                            required: _('Campo requerido'),
                            validate: (value, formValues) => {
                                if (value?.isAfter(formValues.harvestDate))
                                    return _('La fecha de siembra debe ser anterior a la fecha de cosecha.');
                                if (plantingDateBounds.minDate && plantingDateBounds.maxDate && value) {
                                    if (
                                        value.isBefore(plantingDateBounds.minDate, 'day') ||
                                        value.isAfter(plantingDateBounds.maxDate, 'day')
                                    ) {
                                        return _(
                                            'La fecha de siembra solo puede moverse hasta 30 días antes o después de la fecha original.'
                                        );
                                    }
                                }
                                return true;
                            },
                        }}
                        render={({ field: plantingField, fieldState }) => (
                            <FormControl>
                                <MuiDatePicker
                                    {...plantingField}
                                    disabled={plantingDisabled}
                                    onChange={(e) => {
                                        plantingField.onChange(e);
                                        form.setValue('plantingResultId', null);
                                        form.setValue('plantingRequestId', null);
                                    }}
                                    minDate={plantingDateBounds.minDate}
                                    maxDate={plantingMaxDate}
                                    slotProps={{
                                        textField: {
                                            placeholder: _('Seleccione una fecha'),
                                            error: Boolean(fieldState.error?.message),
                                        },
                                    }}
                                    defaultValue={null}
                                />
                                <Typography variant='caption' color='error' sx={{ ml: '14px', mt: '3px' }}>
                                    {fieldState.error?.message}
                                </Typography>
                            </FormControl>
                        )}
                    />
                )}
            </Stack>

            <Stack>
                <Typography color='primary' variant='subtitle1' fontWeight='bold'>
                    {_('Fecha de cosecha')}
                </Typography>
                {renderHarvestDateSelector && (
                    <Controller
                        name='harvestResultId'
                        control={form.control}
                        rules={{ required: _('Campo requerido') }}
                        render={({ field: harvestField, fieldState }) => (
                            <FormControl>
                                <Select
                                    {...harvestField}
                                    placeholder={_('Seleccione una fecha')}
                                    onChange={(e) => {
                                        const selected = harvestDateOptions.find((opt) => opt.value === e.target.value);
                                        if (selected) {
                                            form.setValue('harvestDate', selected.date);
                                            form.setValue('harvestRequestId', selected.requestId);
                                            harvestField.onChange(e);
                                        }
                                    }}
                                    error={Boolean(fieldState.error?.message)}
                                    defaultValue={null}
                                    sx={{ '& .MuiSelect-select': { whiteSpace: 'unset !important' } }}
                                >
                                    {harvestDateOptions.map((pd) => (
                                        <MenuItem key={pd.value} value={pd.value}>
                                            {pd.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <Typography variant='caption' color='error' sx={{ ml: '14px', mt: '3px' }}>
                                    {fieldState.error?.message}
                                </Typography>
                            </FormControl>
                        )}
                    />
                )}
                {!renderHarvestDateSelector && (
                    <Controller
                        name='harvestDate'
                        control={form.control}
                        rules={{
                            required: _('Campo requerido'),
                            validate: (value, formValues) => {
                                if (value?.isBefore(formValues.plantingDate))
                                    return _('La fecha de cosecha debe ser posterior a la fecha de siembra.');
                                return true;
                            },
                        }}
                        render={({ field: harvestField, fieldState }) => (
                            <FormControl>
                                <MuiDatePicker
                                    {...harvestField}
                                    onChange={(e) => {
                                        harvestField.onChange(e);
                                        form.setValue('harvestResultId', null);
                                        form.setValue('harvestRequestId', null);
                                    }}
                                    minDate={
                                        plantingDateOptions.length > 0
                                            ? (form.watch('plantingDate') ?? undefined)
                                            : undefined
                                    }
                                    maxDate={dayjs()}
                                    slotProps={{
                                        textField: {
                                            placeholder: _('Seleccione una fecha'),
                                            error: Boolean(fieldState.error?.message),
                                        },
                                    }}
                                    defaultValue={null}
                                />
                                <Typography variant='caption' color='error' sx={{ ml: '14px', mt: '3px' }}>
                                    {fieldState.error?.message}
                                </Typography>
                            </FormControl>
                        )}
                    />
                )}
            </Stack>

            <Stack>
                <Controller
                    name='yield'
                    control={form.control}
                    rules={{ required: _('Campo requerido') }}
                    render={({ field: yieldField, fieldState }) => (
                        <EnvironmentInput
                            valueType={_('rendimiento')}
                            {...yieldField}
                            crop={cropMonitoring?.crop as CropType}
                            value={yieldField.value || undefined}
                            onChange={yieldField.onChange}
                            units={units}
                            error={fieldState.error?.message}
                            minEnvironment={MIN_ENVIRONMENT}
                            maxEnvironment={MAX_ENVIRONMENT}
                        />
                    )}
                />
            </Stack>

            <Stack>
                <Typography color='primary' variant='subtitle1' fontWeight='bold'>
                    {_('Humedad al cosechar')}
                </Typography>
                <Controller
                    name='humidity'
                    control={form.control}
                    rules={{ required: _('Campo requerido') }}
                    render={({ field: humidityField, fieldState }) => (
                        <TextField
                            {...humidityField}
                            type='number'
                            InputProps={{ endAdornment: <InputAdornment position='end'>%</InputAdornment> }}
                            error={fieldState.error?.message !== undefined}
                            disabled={renderHarvestDateSelector}
                        />
                    )}
                />
            </Stack>
        </Stack>
    );
};

export default CreateReportForm;
